import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'react-bootstrap';
import snakeCaseKeys from 'snakecase-keys';
import { debounceIndexDncRequest, createDncRequest, updateDncRequest, showDncRequest } from '../../../requests/dncs';
import { camelCaseEmptyStringRecursive } from '../../../services/utils';
import DncDatatable from '../../DNC/DncDatatable';
import columnDnc from '../../DNC/columnDnc';
import DashboardDncFilter from '../../DNC/DashboardDncFilter';
import DncForm from '../../DNC/DncForm';
import DncShow from '../../DNC/DncShow';
import { basicDnc } from '../../DNC/dnc';

const TeamDncIndex = ({ currentEmployee, employees }) => {
  const [mode, setMode] = useState('list');
  const [dncs, setDncs] = useState([]);
  const [amount, setAmount] = useState(0);
  const [moreData, setMoreData] = useState(false);
  const [onRequest, setOnRequest] = useState(false);
  const [customParams, setCustomParams] = useState({
    filter_direct_leader_ids: [currentEmployee],
    filter_year: new Date().getFullYear()
  });
  const [selectedDnc, setSelectedDnc] = useState(basicDnc);
  const [hasSearched, setHasSearched] = useState(false);
  const dispatch = useDispatch();

  const handleIndexRequest = useCallback(
    params => {
      if (!hasSearched) return;
      setOnRequest(true);
      const sendParams = {
        ...params,
        ...customParams
      };
      debounceIndexDncRequest({
        dispatch,
        params: sendParams,
        successCallback: response => {
          const { amount: responseAmount } = response.data.metadata;
          setDncs(response.data.data);
          setAmount(responseAmount);
        },
        callback: () => setOnRequest(false)
      });
    },
    [customParams, dispatch, hasSearched]
  );

  useEffect(() => {
    if (mode === 'list' && hasSearched) {
      handleIndexRequest({ page: 1, per_page: 15 });
    }
  }, [mode, customParams, moreData, handleIndexRequest, hasSearched]);

  const handleFilterAdvance = values => {
    const cleanedValues = Object.entries(values).reduce((acc, [key, value]) => {
      if (value !== '' && value !== null && (Array.isArray(value) ? value.length > 0 : true)) {
        acc[key] = value;
      }
      return acc;
    }, {});

    const params = snakeCaseKeys(cleanedValues);
    if (!params.filter_employee_ids) {
      params.filter_direct_leader_ids = [currentEmployee];
    }

    setHasSearched(true);
    setCustomParams(params);
    setMoreData(prev => !prev);
  };

  const handleActions = (item, action) => {
    if (action === 'edit' || action === 'show') {
      setOnRequest(true);
      showDncRequest(item.id, {
        dispatch,
        successCallback: res => {
          setSelectedDnc(camelCaseEmptyStringRecursive(res.data));
          setMode(action);
        },
        callback: () => setOnRequest(false)
      });
    } else if (action === 'new') {
      setSelectedDnc({ ...basicDnc, employeeId: '' });
      setMode('new');
    }
  };

  const handleSubmit = (values, setSubmitting) => {
    const params = snakeCaseKeys(values);
    const id = mode === 'edit' ? selectedDnc.id : null;

    if (mode === 'new') {
      createDncRequest({
        dispatch,
        params,
        successCallback: () => {
          setMode('list');
          setMoreData(!moreData);
        },
        callback: () => setSubmitting(false)
      });
    } else {
      updateDncRequest(id, {
        dispatch,
        params,
        successCallback: () => {
          setMode('list');
          setMoreData(!moreData);
        },
        callback: () => setSubmitting(false)
      });
    }
  };

  const employeeParams = useMemo(() => ({ employees_belong_boss: currentEmployee }), [currentEmployee]);

  if (mode === 'new' || mode === 'edit') {
    return (
      <div className="mt-3">
        <Button variant="link" onClick={() => setMode('list')} className="mb-3 p-0 text-dark">
          {'< Volver'}
        </Button>
        <DncForm dnc={selectedDnc} action={mode} formRequest={handleSubmit} employeeParams={employeeParams} />
      </div>
    );
  }

  if (mode === 'show') {
    return (
      <div className="mt-3">
        <Button variant="link" onClick={() => setMode('list')} className="mb-3 p-0 text-dark">
          {'< Volver'}
        </Button>
        <DncShow match={{ params: { id: selectedDnc.id }, path: '/profile/dnc' }} withBackButton={false} />
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-end my-2">
        <Button
          size="sm"
          style={{ backgroundColor: '#1fb58f', borderColor: '#1fb58f' }}
          onClick={() => handleActions(null, 'new')}
        >
          NUEVO
        </Button>
      </div>
      <DashboardDncFilter employees={employees.filter(e => e.id !== '')} formRequest={handleFilterAdvance} />

      <div className="data-table-yellow mt-3">
        <DncDatatable
          dncs={dncs}
          amount={amount}
          onRequest={onRequest}
          handleIndexRequest={handleIndexRequest}
          customParams={customParams}
          moreData={moreData}
          setMoreData={setMoreData}
          columnDnc={columnDnc}
          clickHandler={handleActions}
          handleMassAction={() => {}}
        />
      </div>
    </>
  );
};

export default TeamDncIndex;
